import { useState } from 'react'
import { FutureTariffBannerProps } from './types'
import dayjs from 'dayjs'
import { ZAPIER_RENEWAL_WEBHOOK } from './constants.ts'
import { ENERGY_TYPES, PRODUCTS_CONFIG, ServiceContract } from '@boltenergy-be/shared'
import { trackEvent } from 'utils/tracking.ts'
import { log } from 'utils/logging.ts'
import { Flow } from 'types/logging.ts'
import { useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import { Banner, Button, Heading, popErrorToast, popSuccessToast } from '@boltenergy-be/design-system'
import { useTranslation } from 'react-i18next'
import { UrlSearchParamsKeys } from 'store/app/types.ts'
import styles from 'components/current-tariffs-card/components/new-tariff-banner/NewTariffBanner.module.scss'
import { DATE_FORMAT } from 'constants/constants.ts'
import Link from 'components/link/Link.tsx'

const NewTariffBanner = ({ serviceContracts, priceLists, newProduct }: FutureTariffBannerProps) => {
  // Redux
  const { urlSearchParams } = useStoreSelector((store) => store.app)

  // i18n
  const { t } = useTranslation(['tariffs', 'errors', 'addresses'])
  const language = useGetLanguage()

  // Local state
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false)
  const [isErrored, setIsErrored] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  // Constants
  const futureProductConfig = newProduct ? PRODUCTS_CONFIG[newProduct] : null
  const mostFuturePriceListElectricity = priceLists[ENERGY_TYPES.electricity]
  const mostFuturePriceListGas = priceLists[ENERGY_TYPES.gas]
  const withFixedConfirm = urlSearchParams[UrlSearchParamsKeys.FIXED_PRICE_RENEWAL_CONFIRMATION] === 'true'

  /**
   * Confirms the fixed tariff renewal for a single service contract
   * @param {ServiceContract} serviceContract
   * @returns {Promise<void>}
   */
  const confirmSingleFixedTariffRenewal = async (serviceContract: ServiceContract): Promise<boolean> => {
    try {
      const formData = new FormData()
      formData.append('contractNumber', serviceContract.contractNumber)
      formData.append('confirmationDate', dayjs().format())

      await fetch(ZAPIER_RENEWAL_WEBHOOK, {
        method: 'POST',
        mode: 'no-cors',
        body: formData
      })

      trackEvent(
        {
          category: 'addresses',
          object: 'fixed_tariff_renewal',
          action: 'confirm'
        },
        { type: serviceContract.detail.energyType }
      )

      return true
    } catch (error) {
      log({ error: error as Error, identifier: `[${Flow.CONTRACT}:confirmFixedTariffRenewal]` })
      return false
    }
  }

  /**
   * Confirms the fixed tariff renewal
   */
  const confirmFixedTariffRenewals = async () => {
    setIsLoading(true)

    try {
      if (serviceContracts) {
        const res = await Promise.all(
          Object.values(serviceContracts)
            .filter(Boolean)
            .map(async (serviceContract) => await confirmSingleFixedTariffRenewal(serviceContract))
        )

        if (res.every(Boolean)) {
          setIsConfirmed(true)
          popSuccessToast(
            t(($) => $.myContract.tariff.confirmed, {
              ns: 'addresses'
            })
          )
          return
        }
      }

      setIsErrored(true)
      popErrorToast(
        t(($) => $.default, {
          ns: 'errors'
        })
      )
    } catch (e) {
      setIsErrored(true)
      popErrorToast(
        t(($) => $.default, {
          ns: 'errors'
        })
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    !!mostFuturePriceListElectricity &&
    futureProductConfig && (
      <Banner
        as="section"
        type="informative"
        title={t(($) => $.overview.futureTariff.title, {
          context: mostFuturePriceListElectricity?.fromDate ? 'withDate' : 'withoutDate',
          date: dayjs(mostFuturePriceListElectricity?.fromDate).format(DATE_FORMAT),
          interpolation: { escapeValue: false }
        })}
        className={styles['new-tariff-card']}
        id={withFixedConfirm ? 'new-tariff-confirm' : 'new-tariff'}
      >
        <Heading as="h2" variant="h4" serif className="mt-300">
          {futureProductConfig.name[language].toLowerCase().startsWith('plenty')
            ? futureProductConfig.name[language]
            : `Bolt ${futureProductConfig.name[language]}`}
        </Heading>

        {(!!mostFuturePriceListElectricity?.url || !!mostFuturePriceListGas?.url) && (
          <div className="flex-row gap-500 mt-100">
            {!!mostFuturePriceListElectricity?.url && (
              <Link
                as="a"
                href={
                  !mostFuturePriceListElectricity?.url?.startsWith('https://')
                    ? `https://${mostFuturePriceListElectricity.url}`
                    : mostFuturePriceListElectricity.url
                }
                download
                target="_blank"
                trailingIcon="download"
                className={styles.download}
              >
                {t(($) => $.products.downloads.electricity)}
              </Link>
            )}

            {!!mostFuturePriceListGas?.url && (
              <Link
                as="a"
                href={
                  !mostFuturePriceListGas?.url?.startsWith('https://')
                    ? `https://${mostFuturePriceListGas.url}`
                    : mostFuturePriceListGas.url
                }
                download
                target="_blank"
                trailingIcon="download"
                className={styles.download}
              >
                {t(($) => $.products.downloads.gas)}
              </Link>
            )}
          </div>
        )}

        {withFixedConfirm && !isConfirmed && !isErrored && (
          <Button onClick={confirmFixedTariffRenewals} className="mt-400" loading={isLoading}>
            {t(($) => $.myContract.tariff.confirmAction, {
              ns: 'addresses'
            })}
          </Button>
        )}
      </Banner>
    )
  )
}

export default NewTariffBanner
